import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { MeContext } from "./AppShell";

export function Team() {
  const { me } = useOutletContext<MeContext>();
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ["team"], queryFn: api.team });
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canManage = me?.org.role === "owner" || me?.org.role === "admin";

  const invite = useMutation({
    mutationFn: () => api.invite({ email, role: "member" }),
    onSuccess: () => {
      setEmail("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["team"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Invite failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });

  const [orgName, setOrgName] = useState(me?.org.name ?? "");
  const rename = useMutation({
    mutationFn: () => api.renameOrg(orgName),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Team</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Who can sign in to {me?.org.name} and use its tools.
        </p>
      </div>

      {canManage && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium">Organisation name</label>
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={me?.org.name}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => rename.mutate()}
              disabled={!orgName.trim() || orgName === me?.org.name || rename.isPending}
            >
              {rename.isPending ? "Saving…" : "Save"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="sr-only">
      </div>

      {canManage && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium">Invite by email</label>
              <Input
                type="email"
                placeholder="tech@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button
              onClick={() => invite.mutate()}
              disabled={!email || invite.isPending}
            >
              {invite.isPending ? "Inviting…" : "Send invite"}
            </Button>
          </CardContent>
        </Card>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>Member</span>
          <span>Role</span>
          <span></span>
        </div>
        {team.data?.members.map((m) => (
          <div
            key={m.id}
            className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-5 py-3 last:border-0"
          >
            <div>
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-xs text-muted-foreground">{m.email}</div>
            </div>
            <Badge variant={m.role === "owner" ? "primary" : "muted"}>
              {m.role}
            </Badge>
            {canManage && m.role !== "owner" ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove.mutate(m.id)}
              >
                Remove
              </Button>
            ) : (
              <span />
            )}
          </div>
        ))}
        {team.data?.invites.map((i) => (
          <div
            key={i.id}
            className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-5 py-3 last:border-0"
          >
            <div className="text-sm text-muted-foreground">{i.email}</div>
            <Badge variant="warn">invited</Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                navigator.clipboard?.writeText(
                  `${window.location.origin}/accept-invite?token=${i.id}`,
                )
              }
            >
              Copy invite link
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
}
